import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  InsertDriveFile as InsertDriveFileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  VideoFile as VideoFileIcon,
  Audiotrack as AudiotrackIcon,
  Archive as ArchiveIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { getAttachmentDetails } from '../services/emailService';
import { getMessageAttachmentDetails } from '../services/messageAttachmentService';
import { useToast } from '../contexts/ToastContext';
import { STORAGE_KEYS, DOWNLOAD_FILE } from '../helpers/constants';

// Internal message attachments may have optional file_type/filename/file_size
export interface EmailAttachment {
  attachment_id: string;
  filename?: string;
  file_type?: string;
  file_size?: number;
  storage_path?: string;
  is_inline?: boolean;
  content_id?: string | null;
}

interface EmailAttachmentsProps {
  attachments: EmailAttachment[];
}

// Treat value as display name only if it looks like a filename (not an attachment id / UUID / numeric id)
const isLikelyFilename = (value: string | undefined): boolean => {
  if (!value || value.length < 4) return false;
  // UUID-like: hex with hyphens or long hex without extension
  const looksLikeUuidOrHexId = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value) ||
    /^[a-f0-9]{20,}$/i.test(value);
  // Long numeric id (e.g. 20280313127856555249) often returned by message channel APIs
  const looksLikeNumericId = /^\d{12,}$/.test(value);
  const hasExtension = /\.[a-z0-9]+$/i.test(value);
  return hasExtension || (!looksLikeUuidOrHexId && !looksLikeNumericId);
};

// Format file size utility (handles undefined for internal message attachments)
const formatFileSize = (bytes: number | undefined): string => {
  const n = bytes ?? 0;
  if (n === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return Math.round(n / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Get file icon based on file type (guards against undefined from internal message attachments)
const getFileIcon = (fileType: string | undefined, filename: string | undefined) => {
  const lowerType = (fileType ?? '').toLowerCase();
  const lowerName = (filename ?? '').toLowerCase();

  if (lowerType.includes('pdf') || lowerName.endsWith('.pdf')) {
    return <PdfIcon sx={{ fontSize: 20, color: '#d32f2f' }} />;
  }
  if (lowerType.includes('image') || /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(lowerName)) {
    return <ImageIcon sx={{ fontSize: 20, color: '#1976d2' }} />;
  }
  if (lowerType.includes('video') || /\.(mp4|avi|mov|wmv)$/i.test(lowerName)) {
    return <VideoFileIcon sx={{ fontSize: 20, color: '#7b1fa2' }} />;
  }
  if (lowerType.includes('audio') || /\.(mp3|wav|ogg)$/i.test(lowerName)) {
    return <AudiotrackIcon sx={{ fontSize: 20, color: '#f57c00' }} />;
  }
  if (lowerType.includes('zip') || lowerType.includes('rar') || lowerType.includes('7z') || 
      /\.(zip|rar|7z|tar|gz)$/i.test(lowerName)) {
    return <ArchiveIcon sx={{ fontSize: 20, color: '#616161' }} />;
  }
  if (lowerType.includes('spreadsheet') || lowerType.includes('excel') || 
      /\.(xls|xlsx|csv)$/i.test(lowerName)) {
    return <TableChartIcon sx={{ fontSize: 20, color: '#2e7d32' }} />;
  }
  if (lowerType.includes('code') || 
      (lowerType.includes('text') && !lowerType.includes('plain')) ||
      /\.(js|ts|jsx|tsx|html|css|json|xml)$/i.test(lowerName)) {
    return <CodeIcon sx={{ fontSize: 20, color: '#424242' }} />;
  }
  if (lowerType.includes('word') || lowerType.includes('document') || 
      lowerType.includes('text/plain') ||
      /\.(doc|docx|rtf|txt)$/i.test(lowerName)) {
    return <DescriptionIcon sx={{ fontSize: 20, color: '#1976d2' }} />;
  }
  
  return <InsertDriveFileIcon sx={{ fontSize: 20, color: '#757575' }} />;
};

// Resolved metadata for attachments that only had id (e.g. message channel API)
const getResolvedFilename = (data: any): string | undefined =>
  data?.filename ?? data?.file_name ?? data?.metadata?.original_filename ?? data?.original_filename;
const getResolvedFileSize = (data: any): number | undefined =>
  data?.file_size ?? data?.size_bytes;

const EmailAttachments: React.FC<EmailAttachmentsProps> = ({ attachments }) => {
  const muiTheme = useTheme();
  const { showError } = useToast();
  const [loadingAttachmentId, setLoadingAttachmentId] = useState<string | null>(null);
  // Cache resolved filename/file_size for attachments that had only id (message channels)
  const [resolvedMeta, setResolvedMeta] = useState<Record<string, { filename?: string; file_size?: number }>>({});

  // Resolve filename for attachments that don't have a displayable name (email API vs message/regular channel API)
  useEffect(() => {
    if (!attachments?.length) return;
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    attachments.forEach((att) => {
      const displayName = att.filename;
      if (displayName && isLikelyFilename(displayName)) return; // Already have a good name
      const aid = att.attachment_id;
      if (resolvedMeta[aid] !== undefined) return; // Already resolved or attempted
      const applyMeta = (data: any) => {
        const filename = getResolvedFilename(data);
        const fileSize = getResolvedFileSize(data);
        if (filename || fileSize !== undefined) {
          setResolvedMeta((prev) => ({ ...prev, [aid]: { filename, file_size: fileSize } }));
        }
      };
      // Try email API first (email channels), then main API (message/regular channels)
      getAttachmentDetails(aid, token, false)
        .then((res) => applyMeta(res?.data))
        .catch(() => {
          getMessageAttachmentDetails(aid, token)
            .then((data) => applyMeta(data))
            .catch(() => setResolvedMeta((prev) => ({ ...prev, [aid]: {} })));
        });
    });
  }, [attachments?.map((a) => a.attachment_id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const handleAttachmentClick = async (attachment: EmailAttachment) => {
    if (loadingAttachmentId) return; // Prevent multiple clicks

    setLoadingAttachmentId(attachment.attachment_id);
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

    try {
      // Fetch attachment details (API returns presigned_url and/or file_url)
      const response = await getAttachmentDetails(
        attachment.attachment_id,
        token,
        true // includePresignedUrl = true
      );

      // Only when VITE_DOWNLOAD_FILE is true and API returned file_url do we use file_url; otherwise use presigned_url (existing behavior)
      const useFileUrl = DOWNLOAD_FILE && !!response.data?.file_url;
      const downloadUrl = useFileUrl ? response.data!.file_url! : response.data?.presigned_url;

      if (downloadUrl) {
        if (useFileUrl) {
          // Flag true + file_url: force download via fetch + blob with correct filename (existing presigned_url path not used here)
          const filename = response.data?.original_filename ?? response.data?.metadata?.original_filename ?? response.data?.filename ?? response.data?.file_name ?? attachment.filename ?? `attachment-${attachment.attachment_id}`;
          const res = await fetch(downloadUrl, { mode: 'cors', credentials: 'omit' });
          if (!res.ok) throw new Error(`Download failed: ${res.status}`);
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        } else {
          // Existing behavior: presigned_url — open in new window (unchanged)
          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        console.error('Attachment response:', response);
        showError('Failed to get attachment URL. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching attachment:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to open attachment. Please try again.';
      showError(errorMessage);
    } finally {
      setLoadingAttachmentId(null);
    }
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        pt: 1.5,
        borderTop: `1px solid ${muiTheme.palette.divider}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 1,
          color: muiTheme.palette.text.secondary,
          fontSize: '0.75rem',
          fontWeight: 500,
        }}
      >
        Attachments ({attachments.length})
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {attachments.map((attachment) => {
          const isLoading = loadingAttachmentId === attachment.attachment_id;
          const isInline = attachment.is_inline === true;
          // Use resolved metadata when message only had id (e.g. message channels)
          const resolved = resolvedMeta[attachment.attachment_id];
          const displayFilename = isLikelyFilename(attachment.filename)
            ? attachment.filename
            : (resolved?.filename && isLikelyFilename(resolved.filename)
                ? resolved.filename
                : undefined);
          const displaySize = attachment.file_size ?? resolved?.file_size;

          // Skip inline attachments (they're embedded in email content)
          if (isInline) {
            return null;
          }

          return (
            <Chip
              key={attachment.attachment_id}
              icon={
                isLoading ? (
                  <CircularProgress size={16} sx={{ color: 'inherit' }} />
                ) : (
                  getFileIcon(attachment.file_type, displayFilename ?? attachment.filename)
                )
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={displayFilename ?? 'Attachment'}
                  >
                    {displayFilename ?? 'Attachment'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      color: muiTheme.palette.text.secondary,
                    }}
                  >
                    ({formatFileSize(displaySize)})
                  </Typography>
                </Box>
              }
              onClick={() => handleAttachmentClick(attachment)}
              disabled={isLoading}
              sx={{
                cursor: isLoading ? 'wait' : 'pointer',
                height: 'auto',
                py: 0.5,
                '&:hover': {
                  backgroundColor: isLoading
                    ? undefined
                    : muiTheme.palette.action.hover,
                },
                '& .MuiChip-label': {
                  padding: '4px 8px',
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default EmailAttachments;

